type Func<T> = (value: BaseBlock, index: number, array: BaseBlock[]) => T

export class BlockSet {
  constructor(public blocks: BaseBlock[] = []) {}

  push(block: BaseBlock) {
    if (block instanceof ElidedBlock && this.blocks.length > 0) {
      const lastBlock = this.blocks.at(-1)
      if (lastBlock instanceof ElidedBlock) {
        lastBlock.push(block)
        return
      }
    }

    this.blocks.push(block)
  }

  getBlocks() {
    return this.blocks
  }

  getRegions() {
    return this.blocks.map(block => block.toRegion())
  }

  map<T, U = this>(func: Func<T>, thisarg?: U) {
    // eslint-disable-next-line unicorn/no-array-method-this-argument
    return this.blocks.map(func, thisarg)
  }

  forEach<T, U = this>(func: Func<T>, thisarg?: U) {
    // eslint-disable-next-line unicorn/no-array-method-this-argument
    return this.blocks.forEach(func, thisarg)
  }

  get length() {
    return this.blocks.length
  }

  get totalWidthPx() {
    return this.blocks.length > 0
      ? this.blocks.map(blocks => blocks.widthPx).reduce((a, b) => a + b)
      : 0
  }

  get totalWidthPxWithoutBorders() {
    return this.blocks.length > 0
      ? this.blocks
          .filter(block => block.variant !== 'boundary')
          .map(blocks => blocks.widthPx)
          .reduce((a, b) => a + b)
      : 0
  }

  get offsetPx() {
    return this.blocks.length > 0 ? this.blocks[0].offsetPx : 0
  }

  get contentBlocks() {
    return this.blocks.filter(block => block instanceof ContentBlock)
  }

  get totalBp() {
    return this.contentBlocks
      .map(block => block.end - block.start)
      .reduce((a, b) => a + b, 0)
  }
}

export class BaseBlock {
  public regionNumber?: number

  public reversed?: boolean

  public refName: string

  public start: number

  public end: number

  public assemblyName: string

  public key: string

  public offsetPx: number

  public widthPx = 0

  public variant?: string

  public isLeftEndOfDisplayedRegion?: boolean

  /**
   * a block that should be shown as filled with data
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(data: any) {
    Object.assign(this, data)
    this.assemblyName = data.assemblyName
    this.refName = data.refName
    this.start = data.start
    this.end = data.end
    this.key = data.key
    this.offsetPx = data.offsetPx
  }

  /**
   * rename the reference sequence of this block and return a new one
   *
   * @param refName -
   * @returns either a new block with a renamed reference sequence,
   * or the same block, if the ref name is not actually different
   */
  renameReference(refName: string) {
    if (this.refName && refName !== this.refName) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return new (this.constructor as any)({ ...this, refName })
    }
    return this
  }

  toRegion() {
    return {
      refName: this.refName,
      start: this.start,
      end: this.end,
      assemblyName: this.assemblyName,
      reversed: this.reversed,
    }
  }
}

export class ContentBlock extends BaseBlock {}

/**
 * marker block representing one or more blocks that are
 * too small to be shown at the current zoom level
 */
export class ElidedBlock extends BaseBlock {
  public widthPx: number

  public elidedBlockCount = 0

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(data: Record<string, any>) {
    super(data)
    this.widthPx = data.widthPx
  }

  push(otherBlock: ElidedBlock) {
    this.elidedBlockCount += 1

    if (otherBlock) {
      this.refName = ''
      this.start = 0
      this.end = 0
      this.widthPx += otherBlock.widthPx
    }
  }
}

/**
 * marker block that sits between two different displayed regions
 * and provides a thick border between them
 */
export class InterRegionPaddingBlock extends BaseBlock {}
